import { useEffect, useRef, useCallback } from 'react';

import logger from '@/utils/logger';

export interface UserTalkingDetectionConfig {
  silenceTimeout?: number;
  enableAutoDetection?: boolean;
}

export interface UserTalkingDetectionCallbacks {
  onUserStartTalking?: () => void;
  onUserStopTalking?: () => void;
  onTalkingStateChange?: (isTalking: boolean) => void;
}

const DEFAULT_CONFIG: Required<UserTalkingDetectionConfig> = {
  silenceTimeout: 3000, // 3秒静默后认为用户停止说话
  enableAutoDetection: true,
};

/**
 * 用户说话检测 Hook
 * 基于消息历史检测用户是否正在说话，实现自动状态管理
 */
export function useUserTalkingDetection(
  msgHistory: any[],
  userId: string | undefined,
  config: UserTalkingDetectionConfig = {},
  callbacks: UserTalkingDetectionCallbacks = {}
): void {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const userTalkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastProcessedMsgRef = useRef<number>(0);

  const { onUserStartTalking, onUserStopTalking, onTalkingStateChange } = callbacks;

  // 清理定时器
  const clearTalkingTimeout = useCallback((): void => {
    if (userTalkingTimeoutRef.current) {
      clearTimeout(userTalkingTimeoutRef.current);
      userTalkingTimeoutRef.current = null;
    }
  }, []);

  // 设置用户开始说话
  const setUserTalking = useCallback(
    (isTalking: boolean): void => {
      logger.info(`[UserTalkingDetection] 用户说话状态变更: ${isTalking}`);

      onTalkingStateChange?.(isTalking);

      if (isTalking) {
        onUserStartTalking?.();
      } else {
        onUserStopTalking?.();
      }
    },
    [onTalkingStateChange, onUserStartTalking, onUserStopTalking]
  );

  // 处理新消息
  const handleNewMessage = useCallback(
    (lastMsg: any): void => {
      if (!lastMsg || !userId) return;

      // 检查是否是当前用户的消息
      if (lastMsg.user === userId) {
        logger.info('[UserTalkingDetection] 检测到用户新消息');

        // 设置用户正在说话
        setUserTalking(true);

        // 清除之前的定时器
        clearTalkingTimeout();

        // 设置静默定时器
        userTalkingTimeoutRef.current = setTimeout(() => {
          logger.debug('[UserTalkingDetection] 静默超时，用户停止说话');
          setUserTalking(false);
        }, finalConfig.silenceTimeout);
      }
    },
    [userId, setUserTalking, clearTalkingTimeout, finalConfig.silenceTimeout]
  );

  // 监听消息历史变化
  useEffect(() => {
    if (!finalConfig.enableAutoDetection || !msgHistory || !userId) {
      return;
    }

    // 检查是否有新消息
    if (msgHistory.length > lastProcessedMsgRef.current) {
      const lastMsg = msgHistory[msgHistory.length - 1];
      lastProcessedMsgRef.current = msgHistory.length;

      handleNewMessage(lastMsg);
    }
  }, [msgHistory, userId, finalConfig.enableAutoDetection, handleNewMessage]);

  // 用户ID变化时重置状态
  useEffect(() => {
    if (userId) {
      logger.info('[UserTalkingDetection] 用户ID变化，重置检测状态');
      clearTalkingTimeout();
      lastProcessedMsgRef.current = 0;
      setUserTalking(false);
    }
  }, [userId, clearTalkingTimeout, setUserTalking]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      logger.info('[UserTalkingDetection] 清理用户说话检测');
      clearTalkingTimeout();
      setUserTalking(false);
    };
  }, [clearTalkingTimeout, setUserTalking]);

  // 手动重置检测状态
  const resetTalkingState = useCallback((): void => {
    logger.info('[UserTalkingDetection] 手动重置说话状态');
    clearTalkingTimeout();
    setUserTalking(false);
    lastProcessedMsgRef.current = 0;
  }, [clearTalkingTimeout, setUserTalking]);

  // 页面可见性变化处理
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        logger.info('[UserTalkingDetection] 页面隐藏，停止说话检测');
        clearTalkingTimeout();
        setUserTalking(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearTalkingTimeout, setUserTalking]);

  // 暴露调试方法
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__resetUserTalking = resetTalkingState;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).__resetUserTalking;
      }
    };
  }, [resetTalkingState]);
}
